/**
 * @summary simple linear tweening - no easing, no acceleration
 */
export function linearTween(t: number, b: number, c: number, d: number): number {
  return (c * t) / d + b;
}

/**
 * @summary quadratic easing in - accelerating from zero velocity
 */
export function easeInQuad(t: number, b: number, c: number, d: number): number {
  t /= d;
  return c * t * t + b;
}

/**
 * @summary quadratic easing out - decelerating to zero velocity
 */
export function easeOutQuad(t: number, b: number, c: number, d: number): number {
  t /= d;
  return -c * t * (t - 2) + b;
}

/**
 * @summary quadratic easing in/out - acceleration until halfway, then deceleration
 */
export function easeInOutQuad(t: number, b: number, c: number, d: number): number {
  t /= d / 2;
  if (t < 1) {
    return (c / 2) * t * t + b;
  }
  t--;
  return (-c / 2) * (t * (t - 2) - 1) + b;
}

/**
 * @summary cubic easing in - accelerating from zero velocity
 */
export function easeInCubic(t: number, b: number, c: number, d: number): number {
  t /= d;
  return c * t * t * t + b;
}

/**
 * @summary cubic easing out - decelerating to zero velocity
 */
export function easeOutCubic(t: number, b: number, c: number, d: number): number {
  t /= d;
  t--;
  return c * (t * t * t + 1) + b;
}

/**
 * @summary  cubic easing in/out - acceleration until halfway, then deceleration
 */
export function easeInOutCubic(t: number, b: number, c: number, d: number): number {
  t /= d / 2;
  if (t < 1) {
    return (c / 2) * t * t * t + b;
  }
  t -= 2;
  return (c / 2) * (t * t * t + 2) + b;
}

/**
 * @summary  quartic easing in - accelerating from zero velocity
 */
export function easeInQuart(t: number, b: number, c: number, d: number): number {
  t /= d;
  return c * t * t * t * t + b;
}

/**
 * @summary  quartic easing out - decelerating to zero velocity
 */
export function easeOutQuart(t: number, b: number, c: number, d: number): number {
  t /= d;
  t--;
  return -c * (t * t * t * t - 1) + b;
}

/**
 * @summary  quartic easing in/out - acceleration until halfway, then deceleration
 */
export function easeInOutQuart(t: number, b: number, c: number, d: number): number {
  t /= d / 2;
  if (t < 1) {
    return (c / 2) * t * t * t * t + b;
  }
  t -= 2;
  return (-c / 2) * (t * t * t * t - 2) + b;
}

/**
 * @summary  quintic easing in - accelerating from zero velocity
 */
export function easeInQuint(t: number, b: number, c: number, d: number): number {
  t /= d;
  return c * t * t * t * t * t + b;
}

/**
 * @summary quintic easing out - decelerating to zero velocity
 */
export function easeOutQuint(t: number, b: number, c: number, d: number): number {
  t /= d;
  t--;
  return c * (t * t * t * t * t + 1) + b;
}

/**
 * @summary quintic easing in/out - acceleration until halfway, then deceleration
 */
export function easeInOutQuint(t: number, b: number, c: number, d: number): number {
  t /= d / 2;
  if (t < 1) {
    return (c / 2) * t * t * t * t * t + b;
  }
  t -= 2;
  return (c / 2) * (t * t * t * t * t + 2) + b;
}

/**
 * @summary sinusoidal easing in - accelerating from zero velocity
 */
export function easeInSine(t: number, b: number, c: number, d: number): number {
  return -c * Math.cos((t / d) * (Math.PI / 2)) + c + b;
}

/**
 * @summary sinusoidal easing out - decelerating to zero velocity
 */
export function easeOutSine(t: number, b: number, c: number, d: number): number {
  return c * Math.sin((t / d) * (Math.PI / 2)) + b;
}

/**
 * @summary  sinusoidal easing in/out - accelerating until halfway, then decelerating
 */
export function easeInOutSine(t: number, b: number, c: number, d: number): number {
  return (-c / 2) * (Math.cos((Math.PI * t) / d) - 1) + b;
}

/**
 * @summary  exponential easing in - accelerating from zero velocity
 */
export function easeInExpo(t: number, b: number, c: number, d: number): number {
  return c * Math.pow(2, 10 * (t / d - 1)) + b;
}

/**
 * @summary  exponential easing out - decelerating to zero velocity
 */
export function easeOutExpo(t: number, b: number, c: number, d: number): number {
  return c * (-Math.pow(2, (-10 * t) / d) + 1) + b;
}

/**
 * @summary  exponential easing in/out - accelerating until halfway, then decelerating
 */
export function easeInOutExpo(t: number, b: number, c: number, d: number): number {
  t /= d / 2;
  if (t < 1) {
    return (c / 2) * Math.pow(2, 10 * (t - 1)) + b;
  }
  t--;
  return (c / 2) * (-Math.pow(2, -10 * t) + 2) + b;
}

/**
 * @summary  circular easing in - accelerating from zero velocity
 */
export function easeInCirc(t: number, b: number, c: number, d: number): number {
  t /= d;
  return -c * (Math.sqrt(1 - t * t) - 1) + b;
}

/**
 * @summary  circular easing out - decelerating to zero velocity
 */
export function easeOutCirc(t: number, b: number, c: number, d: number): number {
  t /= d;
  t--;
  return c * Math.sqrt(1 - t * t) + b;
}

/**
 * @summary  circular easing in/out - acceleration until halfway, then deceleration
 */
export function easeInOutCirc(t: number, b: number, c: number, d: number): number {
  t /= d / 2;
  if (t < 1) {
    return (-c / 2) * (Math.sqrt(1 - t * t) - 1) + b;
  }
  t -= 2;
  return (c / 2) * (Math.sqrt(1 - t * t) + 1) + b;
}
